import { AppState, NavEvent, QuickLinks } from '@news-mono/web-common'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { StyledAd } from '../TheNightlyStickySiteHeader/TheNightlyStickySiteHeader.styled'
import { addListener, removeListener } from '../../__helpers/global-dom-events'
import { DimensionProps, withDimensions } from '../../__helpers/with-dimensions'
import { TNNavHeaderWithStickyBanner } from '../../user-registration'
import {
    HeaderDisplayState,
    HeaderScrollType,
    getEndAvailableFutureState,
} from './helper/site-header-state-helper'
import debug from 'debug'

const theNightlyStickySiteHeaderDebug = debug('site-header')
const stateDebug = theNightlyStickySiteHeaderDebug.extend('state')
const scrollPositionDebug = theNightlyStickySiteHeaderDebug.extend('position')

export interface TheNightlyStickySiteHeaderProps {
    headerAd?: (rendered?: () => void) => JSX.Element | undefined
    onEvent: (event: NavEvent) => void

    navigation: JSX.Element
    disableSticky?: boolean
    quickLinks: QuickLinks
}

type InternalProps = TheNightlyStickySiteHeaderProps & DimensionProps

export const InternalTheNightlyStickySiteHeader: React.FC<InternalProps> = (
    props,
) => {
    const renditionType = useSelector(
        (state: AppState) => state.render.renditionType,
    )

    const [currentScrollPos, changeScrollPos] = useState(0)
    const [prevScrollPos, setPrevScrollPos] = useState(0)

    const [isChanging, setChanging] = useState(false)
    const [displayState, changeDisplayState] =
        useState<HeaderDisplayState>('full')
    const [scrollType, changeScrollType] =
        useState<HeaderScrollType>('scroll-down')

    // This will be 0 if the ad wrapper isn't being displayed.
    const adHeight = props.height

    /**
     * Checks viewport scroll level.
     * If user has scrolled over scrollTarget in px it sets the component to hidden and it scrolls off the page.
     * Upon scrolling up, it checks if the component is hidden and brings it back into view
     */

    const handleScroll = () => {
        changeScrollPos(window.scrollY)
    }

    useEffect(() => {
        scrollPositionDebug(
            `Scroll position state update from ${prevScrollPos} to ${currentScrollPos}`,
        )

        const currentScrollState =
            currentScrollPos < prevScrollPos ? 'scroll-up' : 'scroll-down'

        // The stored scroll type has changed from the current scroll type,
        // so we want to make sure it's updated for styling
        if (scrollType !== currentScrollState) {
            stateDebug(
                `Changing scroll direction from '${scrollType}' to '${currentScrollState}'.`,
            )
            changeScrollType(currentScrollState)
        }

        const nextState = getEndAvailableFutureState(
            displayState,
            currentScrollState,
            currentScrollPos,
            prevScrollPos,
            adHeight,
        )

        // no future state was found, so we want to ignore...
        if (nextState === undefined) {
            setPrevScrollPos(currentScrollPos)
            return
        }

        const { futureState } = nextState

        // The user is now scrolling the screen, so we need to set it to isChanging
        // to allow it to start manipulating the animations
        if (!isChanging) {
            setChanging(true)
        }

        changeDisplayState(futureState)
        setPrevScrollPos(currentScrollPos)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentScrollPos])

    useEffect(() => {
        addListener('scroll', handleScroll)
        return () => {
            removeListener('scroll', handleScroll)
        }
    }, [])

    const disableHeaderAd = renditionType === 'app'

    return (
        <>
            {!disableHeaderAd && renderHeaderAd()}
            <TNNavHeaderWithStickyBanner
                adHeight={adHeight}
                isChanging={isChanging}
                scrollType={scrollType}
                displayState={displayState}
                navigation={props.navigation}
                onEvent={props.onEvent}
                quickLinks={props.quickLinks}
            />
        </>
    )

    function renderHeaderAd() {
        return (
            props.headerAd && (
                <StyledAd
                    className="headerAdvertisement"
                    ref={props.innerRef}
                    height={adHeight}
                    isChanging={isChanging}
                    displayState={displayState}
                    scrollType={scrollType}
                >
                    {props.headerAd(() => props.triggerMeasure())}
                </StyledAd>
            )
        )
    }
}

export const TheNightlyStickySiteHeader =
    withDimensions<TheNightlyStickySiteHeaderProps>(
        InternalTheNightlyStickySiteHeader,
    )

TheNightlyStickySiteHeader.displayName = 'TheNightlyStickySiteHeader'
